import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import { withGenericConfig } from "contexts/generic-config-context";
import BadgeComponent from "components/core/badge/badge-component";
import { HEADER_PRODUCT_TAG_HOVERED, HEADER_SUBMENU_LINK_CLICKED } from "/constants/bigbrain-event-types";
import { trackEvent } from "/services/bigbrain-service";
import PictureComponent from "components/core/picture/picture-component";
import classnames from "classnames";
import { ICON_TYPES, TAG_TYPES } from "segments/desktop/header/layouts/base-header/base-header-service";
import { BACKGROUND_GRAY_COLOR_NAME, WHITE_COLOR_NAME, WORK_OS_IRIS_COLOR_NAME } from "styles/color-consts";
import colors from "styles/colors";
import SecondaryButton from "segments/desktop/core-components/button/secondary-button/secondary-button";
import MondayMarkupLanguageComponent from "client/components/system/monday-markup-language/monday-markup-language-component";
import BulletCheck from "static/svg/bullet_checked_green.svg";
import BulletPlus from "static/svg/bullet_plus_green_2.svg";
import { getLocalizedLink } from "constants/localized-links";
import { fireGtmEvent } from "client/services/gtm-service/gtm-service";
import DataLayerEventTypes from "constants/data-layer-event-types";
import { MENU_ID_TO_EVENT_KIND_MAP } from "segments/desktop/header/layouts/base-header/base-header-desktop/consts";
import { StyledHeaderMenuSectionItemComponent } from "./header-menu-section-item-component.scss";
const HeaderMenuSectionItemComponent = (props)=>{
    const { icon: Icon, iconType, iconColor, imageSrc, topic, title, subtitle, subtitleIcon: SubtitleIcon, description, url, isPopular, translate, badgeText, badgeColorName, badgeTextColorName, badgeStrokeColorName, openInNewTab, button, menuId, sectionId, iconStrokeColor, tags, buttonText, outline, itemType, backgroundColor, hoverBackgroundColor, subtitleAboveItem, localeId, imageFloatingCaption } = props;
    const localizedUrl = useMemo(()=>getLocalizedLink(url, localeId), [
        url,
        localeId
    ]);
    const onClick = useCallback((e)=>{
        const isKeyboardEvent = (e === null || e === void 0 ? void 0 : e.screenX) === 0 && (e === null || e === void 0 ? void 0 : e.screenY) === 0;
        fireGtmEvent(DataLayerEventTypes.CLICK_MAIN_NAV, {
            click_text: translate(title),
            parent_category: MENU_ID_TO_EVENT_KIND_MAP[menuId]
        });
        trackEvent(HEADER_SUBMENU_LINK_CLICKED, {
            kind: localizedUrl,
            info1: isKeyboardEvent ? "keyboard" : "mouse",
            info2: menuId,
            info3: sectionId
        });
    }, [
        localizedUrl,
        menuId,
        sectionId,
        title,
        translate
    ]);
    const onTagMouseEnter = (tag, index)=>{
        trackEvent(HEADER_PRODUCT_TAG_HOVERED, {
            kind: translate(title),
            info1: translate(tag),
            info2: index
        });
    };
    const renderMenuItemText = ()=>/*#__PURE__*/ _jsxs("div", {
            className: "menu-item-text-wrapper",
            children: [
                topic && /*#__PURE__*/ _jsx("div", {
                    className: "menu-item-topic-wrapper",
                    children: translate(topic)
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "menu-item-title-wrapper",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "menu-item-title",
                            children: translate(title)
                        }),
                        isPopular && /*#__PURE__*/ _jsx("div", {
                            className: "new-badge-component-wrapper",
                            children: /*#__PURE__*/ _jsx(BadgeComponent, {
                                badgeText: translate(badgeText),
                                badgeColorName: badgeColorName,
                                badgeTextColorName: badgeTextColorName,
                                badgeStrokeColorName: badgeStrokeColorName
                            })
                        })
                    ]
                }),
                subtitle && /*#__PURE__*/ _jsxs("div", {
                    className: "menu-item-subtitle-wrapper",
                    children: [
                        SubtitleIcon && /*#__PURE__*/ _jsx(SubtitleIcon, {}),
                        /*#__PURE__*/ _jsx("span", {
                            className: "menu-item-title",
                            children: translate(subtitle)
                        })
                    ]
                }),
                description && /*#__PURE__*/ _jsx("span", {
                    className: "menu-item-description",
                    children: /*#__PURE__*/ _jsx(MondayMarkupLanguageComponent, {
                        text: translate(description)
                    })
                }),
                button && /*#__PURE__*/ _jsx("div", {
                    className: "menu-item-description-button",
                    children: /*#__PURE__*/ _jsx(SecondaryButton, {
                        buttonText: translate(buttonText || "header.menuItem.button.readMore"),
                        url: localizedUrl,
                        isOpenLinkInNewTab: openInNewTab
                    })
                })
            ]
        });
    const renderTag = ({ tag, index })=>{
        const tagsType = (tags === null || tags === void 0 ? void 0 : tags.tagsType) || TAG_TYPES.DEFAULT;
        const renderBulletTag = ()=>{
            var _tags_list;
            const BulletIcon = index === (tags === null || tags === void 0 ? void 0 : (_tags_list = tags.list) === null || _tags_list === void 0 ? void 0 : _tags_list.length) - 1 ? BulletPlus : BulletCheck;
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(BulletIcon, {
                        className: "tag-bullet"
                    }),
                    translate(tag)
                ]
            });
        };
        return /*#__PURE__*/ _jsx("div", {
            className: "tag",
            onMouseEnter: ()=>onTagMouseEnter(tag, index),
            children: tagsType === TAG_TYPES.BULLET ? renderBulletTag() : translate(tag)
        }, index);
    };
    const renderTags = ({ className })=>{
        return tags && /*#__PURE__*/ _jsxs("div", {
            className: classnames("tags-wrapper", className),
            style: {
                "--header-product-tag-background-color": tags.backgroundColor,
                "--header-product-tag-color": tags.color
            },
            children: [
                tags.title && /*#__PURE__*/ _jsx("div", {
                    className: "tags-title",
                    children: `${translate(tags.title)}:`
                }),
                tags.list.map((tag, index)=>renderTag({
                        tag,
                        index
                    }))
            ]
        });
    };
    const getStyle = ()=>{
        const backgroundColorValue = backgroundColor || colors[WHITE_COLOR_NAME];
        const hoverBackgroundColorValue = hoverBackgroundColor || colors[BACKGROUND_GRAY_COLOR_NAME];
        return {
            "--header-menu-section-item-background-color": backgroundColorValue,
            "--header-menu-section-item-hover-background-color": hoverBackgroundColorValue
        };
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            subtitleAboveItem && /*#__PURE__*/ _jsx("div", {
                children: translate(subtitleAboveItem)
            }),
            /*#__PURE__*/ _jsx(StyledHeaderMenuSectionItemComponent, {
                className: classnames("header-menu-section-item-component-wrapper", itemType, {
                    outline
                }),
                style: getStyle(),
                children: /*#__PURE__*/ _jsx("a", {
                    className: "header-menu-section-item-component",
                    href: localizedUrl,
                    onClick: onClick,
                    target: openInNewTab ? "_blank" : "_self",
                    rel: "noreferrer",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "header-menu-item-content-and-tags-wrapper",
                        style: {
                            display: "flex",
                            flexDirection: "column"
                        },
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: classnames("header-menu-item-content", {
                                    "has-image-instead-title": !!imageSrc,
                                    "no-description": !description
                                }),
                                children: [
                                    Icon && /*#__PURE__*/ _jsx(Icon, {
                                        className: classnames("menu-item-title-icon", iconType),
                                        style: {
                                            "--icon-color": colors[iconColor || WORK_OS_IRIS_COLOR_NAME],
                                            "--icon-stroke-color": iconStrokeColor
                                        }
                                    }),
                                    imageSrc && /*#__PURE__*/ _jsxs("div", {
                                        className: "menu-item-image-and-caption-wrapper",
                                        children: [
                                            /*#__PURE__*/ _jsx(PictureComponent, {
                                                src: imageSrc
                                            }),
                                            imageFloatingCaption && /*#__PURE__*/ _jsx("span", {
                                                className: "menu-item-image-floating-caption",
                                                children: translate(imageFloatingCaption)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "menu-item-text-and-tags-wrapper",
                                        children: [
                                            renderMenuItemText(),
                                            renderTags({
                                                className: "tags-next-to-logo"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            renderTags({
                                className: "tags-at-the-bottom"
                            })
                        ]
                    })
                })
            }, title)
        ]
    });
};
export default withGenericConfig(HeaderMenuSectionItemComponent);
HeaderMenuSectionItemComponent.defaultProps = {
    iconType: ICON_TYPES.outline
};
